import { cn } from "@/utils/cn";
import { ChevronDown, Globe, Link2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface SourceEntry {
  title: string;
  url: string;
}

const getHostname = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const SourceFavicon = ({
  url,
  size = 16,
  className,
}: {
  url: string;
  size?: number;
  className?: string;
}) => {
  const [failed, setFailed] = useState(false);
  const hostname = getHostname(url);

  if (failed) {
    return (
      <Globe
        size={size}
        className={cn("shrink-0 text-muted-foreground", className)}
      />
    );
  }

  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      className={cn("shrink-0 rounded-sm object-contain", className)}
      onError={() => setFailed(true)}
    />
  );
};

const SourceCard = ({
  source,
  index,
}: {
  source: SourceEntry;
  index: number;
}) => {
  const hostname = getHostname(source.url);

  return (
    <motion.a
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 30,
        delay: Math.min(index * 0.02, 0.4),
      }}
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-start gap-2.5 rounded-lg border border-border p-2.5 min-w-0 transition-colors hover:border-primary/50 hover:bg-muted/60"
      style={{ background: "var(--card-background)" }}
    >
      <span className="w-6 h-6 rounded-md bg-muted flex items-center justify-center shrink-0 mt-0.5">
        <SourceFavicon url={source.url} size={14} />
      </span>
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-medium text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
          {source.title}
        </span>
        <span className="text-[10px] text-muted-foreground truncate">
          {hostname}
        </span>
      </span>
      <span className="ms-auto text-[10px] text-muted-foreground/60 shrink-0 font-medium">
        {index + 1}
      </span>
    </motion.a>
  );
};

export const SourcesList = ({ sources }: { sources: Array<SourceEntry> }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (sources.length === 0) {
    return null;
  }

  const previewHostnames = Array.from(
    new Set(sources.map((source) => getHostname(source.url))),
  ).slice(0, 5);

  return (
    <div className="flex flex-col min-w-0 mt-3 pt-3 border-t border-border">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 py-1 w-fit cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
      >
        <Link2 size={13} className="shrink-0" />
        <span className="text-xs font-medium">{t("playground.sources")}</span>
        <span className="text-[10px] font-medium bg-muted px-1.5 py-0.5 rounded-full">
          {sources.length}
        </span>
        <span className="flex items-center -space-x-1.5 rtl:space-x-reverse">
          {previewHostnames.map((hostname) => (
            <span
              key={hostname}
              className="w-4 h-4 rounded-full bg-muted ring-1 ring-background flex items-center justify-center overflow-hidden"
            >
              <SourceFavicon url={`https://${hostname}`} size={12} />
            </span>
          ))}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center"
        >
          <ChevronDown size={13} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
              {sources.map((source, index) => (
                <SourceCard
                  key={`${source.url}-${index}`}
                  source={source}
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
